import { z } from "zod"
import { EmergenceDistribution, SpecialOpsCode, TicketAnchorSlot } from "./special-operation-codes.js"

export const SpecialOpsMediasSchema = z.object({
	desktop: z.url().optional(),
	desktopHp: z.url().optional(),
	mobile: z.url().optional(),
})

export const BannerHPDataSchema = z.object({
	link_desktop: z.url().optional(),
	link_mobile: z.url().nullable().optional(),
	medias: z
		.object({
			desktop: z.url().nullable().optional(),
			mobile: z.url().nullable().optional(),
		})
		.optional(),
})

export const CanalHomeDataSchema = z.object({
	heading1: z.string().nullable().optional(),
	heading2: z.string().nullable().optional(),
	heading3: z.string().nullable().optional(),
})

export const ClassiqueHomeDataSchema = z.object({
	heading1: z.string().nullable().optional(),
	heading2: z.string().nullable().optional(),
	heading3: z.string().nullable().optional(),
	link1_label: z.string().nullable().optional(),
	link1_url: z.url().nullable().optional(),
	link2_label: z.string().nullable().optional(),
	link2_url: z.url().nullable().optional(),
})

export const Club300DataSchema = z.object({
	_placeholder: z.boolean().optional(),
})

export const DisneyHomeDataSchema = z.object({
	medias: z
		.object({
			native_grand_angle: z.object({
				image_url: z.url().nullable().optional(),
				url: z.url().nullable().optional(),
			}),
		})
		.nullable()
		.optional(),
})

export const DolbyHomeDataSchema = z.object({
	_placeholder: z.boolean().optional(),
})

enum SvodProviderEnum {
	AMAZON_PRIME = "amazon-prime",
	APPLETV = "appletv",
	CANALPLAY = "canalplay",
	DISNEY = "disney",
	FILMOTV = "filmotv",
	GLOBOPLAY = "globoplay",
	LIONSGATE_PLUS = "lionsgate-plus",
	MADELEN = "madelen",
	MAX = "max",
	MOVISTAR = "movistar",
	MUBI = "mubi",
	MYCANAL = "mycanal",
	NETFLIX = "netflix",
	OCS = "ocs",
	PARAMOUNTPLUS_PVC = "paramountplus-pvc",
	SALTO = "salto",
	SFR_PLAY = "sfr-play",
	SKY = "sky",
	SKYSHOWTIME = "skyshowtime",
	STAR_PLUS = "star-plus",
	STARZPLAY = "starzplay",
	TELECINE = "telecine",
	TF1PLUS = "tf1plus",
}

export const svodProviderEnumSchema = z.preprocess(
	(val) => (typeof val === "string" ? val.toLowerCase().replace(/_/g, "-") : val),
	z.enum(Object.values(SvodProviderEnum)),
)

export const EmergenceSVODDataSchema = z.object({
	provider: svodProviderEnumSchema.nullable().optional(),
	release_date: z.iso.datetime().optional(),
})
export const EmergenceTheaterDataSchema = z.object({
	release_date: z.iso.datetime().optional(),
})
// The provider rule needs the stored distribution on update, so it lives in resolveEmergenceMovieProvider.
export const EmergenceMovieDataSchema = z.object({
	distribution: z.enum(EmergenceDistribution).optional(),
	provider: svodProviderEnumSchema.nullable().optional(),
	release_date: z.iso.datetime().optional(),
})
export const EmergenceSeriesDataSchema = z.object({
	provider: svodProviderEnumSchema.nullable().optional(),
	release_date: z.iso.datetime().optional(),
})
export const FestivalSectionDataSchema = z.object({
	festivalEditionId: z.number().int().optional(),
	festivalId: z.number().int().optional(),
	playlist: z
		.record(
			z.string(),
			z.object({
				movies: z.array(z.number().int()).optional(),
				series: z.array(z.number().int()).optional(),
			}),
		)
		.nullable()
		.optional(),
})

export const FilmesOnlineDataSchema = z.object({
	heading1: z.string().nullable().optional(),
	heading2: z.string().nullable().optional(),
	heading3: z.string().nullable().optional(),
	heading4: z.string().nullable().optional(),
	heading5: z.string().nullable().optional(),
	heading6: z.string().nullable().optional(),
	heading7: z.string().nullable().optional(),
	heading8: z.string().nullable().optional(),
	heading9: z.string().nullable().optional(),
	heading10: z.string().nullable().optional(),
})

export const IMAXHomeDataSchema = z.object({
	_placeholder: z.boolean().optional(),
})

export const IndelebileHomeDataSchema = z.object({
	heading1: z.string().nullable().optional(),
	heading2: z.string().nullable().optional(),
	heading3: z.string().nullable().optional(),
})

export const M6HomeDataSchema = z.object({
	heading1: z.string().nullable().optional(),
	heading2: z.string().nullable().optional(),
	heading3: z.string().nullable().optional(),
})

export const MaxHomeDataSchema = z.object({
	heading1: z.string().nullable().optional(),
	heading2: z.string().nullable().optional(),
	heading3: z.string().nullable().optional(),
})

export const MobileDdayDataSchema = z.object({
	dday_campaign: z.string().optional(),
	dday_movie_id: z.number().int().optional(),
	dday_movie_list_id: z.array(z.number().int()).nullable().optional(),
	dday_movie_list_title: z.string().nullable().optional(),
	dday_force_uppercase_headers: z.boolean().optional(),
	dday_nav_background_color_light: z.string().nullable().optional(),
	dday_nav_background_color_dark: z.string().nullable().optional(),
	dday_nav_font_color_light: z.string().nullable().optional(),
	dday_nav_font_color_dark: z.string().nullable().optional(),
	dday_main_color_light: z.string().nullable().optional(),
	dday_main_color_dark: z.string().nullable().optional(),
	dday_title_spacing_between_letters: z.number().nullable().optional(),
	dday_title_see_more_title_displayed: z.boolean().optional(),
	dday_background_color: z.string().nullable().optional(),
	dday_force_theme_light: z.boolean().optional(),
	dday_force_theme_dark: z.boolean().optional(),
	dday_status_bar_icons_light: z.boolean().optional(),
	dday_status_bar_icons_dark: z.boolean().optional(),
	dday_segmented_color_light: z.string().nullable().optional(),
	dday_segmented_color_dark: z.string().nullable().optional(),
	dday_segmented_selected_font_color_light: z.string().nullable().optional(),
	dday_segmented_selected_font_color_dark: z.string().nullable().optional(),
	dday_date: z.string().optional(),
	dday_last_date: z.string().optional(),
	dday_assets_version: z.number().int().optional(),
	dday_assets_hash: z.string().optional(),
	dday_font_url: z.url().nullable().optional(),
	dday_font_size: z.number().int().nullable().optional(),
	dday_movie_background_url: z.url().nullable().optional(),
	dday_movie_background_id: z.string().nullable().optional(),
	dday_movie_logo_url: z.url().nullable().optional(),
	dday_movie_logo_id: z.string().nullable().optional(),
	dday_splash_background_url: z.url().nullable().optional(),
	dday_splash_background_id: z.string().nullable().optional(),
	dday_splash_logo_url: z.url().nullable().optional(),
	dday_splash_logo_id: z.string().nullable().optional(),
	dday_splashscreen_status_bar_color_style: z.string().nullable().optional(),
	dday_splashscreen_activity_color: z.string().nullable().optional(),
})

export const NovelasHomeDataSchema = z.object({
	_placeholder: z.boolean().optional(),
})

export const RollerHomeDataSchema = z.object({
	title: z.string().nullable().optional(),
})

export const SponsoredAutocompleteDataSchema = z.object({
	release_date: z.iso.datetime().nullable().optional(),
})

export const TF1HomeDataSchema = z.object({
	heading1: z.string().nullable().optional(),
	heading2: z.string().nullable().optional(),
	heading3: z.string().nullable().optional(),
})

export const TicketAnchorSlotSchema = z.enum(TicketAnchorSlot)

export const TicketAnchorDataSchema = z.object({
	cta_text_after_release: z.string().nullable().optional(),
	cta_text_before_release: z.string().nullable().optional(),
	display_showtimes: z.boolean().optional(),
	link: z.url().optional(),
	medias: SpecialOpsMediasSchema.optional(),
	release_date: z.iso.datetime().optional(),
	slots: z.array(TicketAnchorSlotSchema).nullable().optional(),
	text_after_release: z.string().nullable().optional(),
	text_before_release: z.string().nullable().optional(),
	text_color: z.string().nullable().optional(),
})

export const VODHomeDataSchema = z.object({
	heading1: z.string().nullable().optional(),
	heading2: z.string().nullable().optional(),
})

export type SvodProvider = z.infer<typeof svodProviderEnumSchema>
export { SvodProviderEnum }

export type BannerHPData = z.infer<typeof BannerHPDataSchema>
export type CanalHomeData = z.infer<typeof CanalHomeDataSchema>
export type ClassiqueHomeData = z.infer<typeof ClassiqueHomeDataSchema>
export type Club300Data = z.infer<typeof Club300DataSchema>
export type DisneyHomeData = z.infer<typeof DisneyHomeDataSchema>
export type DolbyHomeData = z.infer<typeof DolbyHomeDataSchema>
export type EmergenceMovieData = z.infer<typeof EmergenceMovieDataSchema>
export type EmergenceSeriesData = z.infer<typeof EmergenceSeriesDataSchema>
export type EmergenceSVODData = z.infer<typeof EmergenceSVODDataSchema>
export type EmergenceTheaterData = z.infer<typeof EmergenceTheaterDataSchema>
export type FestivalSectionData = z.infer<typeof FestivalSectionDataSchema>
export type FilmesOnlineData = z.infer<typeof FilmesOnlineDataSchema>
export type IMAXHomeData = z.infer<typeof IMAXHomeDataSchema>
export type IndelebileHomeData = z.infer<typeof IndelebileHomeDataSchema>
export type M6HomeData = z.infer<typeof M6HomeDataSchema>
export type MaxHomeData = z.infer<typeof MaxHomeDataSchema>
export type MobileDdayData = z.infer<typeof MobileDdayDataSchema>
export type NovelasHomeData = z.infer<typeof NovelasHomeDataSchema>
export type RollerHomeData = z.infer<typeof RollerHomeDataSchema>
export type SponsoredAutocompleteData = z.infer<typeof SponsoredAutocompleteDataSchema>
export type TF1HomeData = z.infer<typeof TF1HomeDataSchema>
export type TicketAnchorData = z.infer<typeof TicketAnchorDataSchema>
export type VODHomeData = z.infer<typeof VODHomeDataSchema>
export type SpecialOpsMedias = z.infer<typeof SpecialOpsMediasSchema>

// The data schema of each code, as stored. Graph validates writes against its `.strict()` form.
export const specialOpsDataSchemaByCode = {
	[SpecialOpsCode.BANNER_HP]: BannerHPDataSchema,
	[SpecialOpsCode.CANAL_HOME]: CanalHomeDataSchema,
	[SpecialOpsCode.CLASSIQUE_HOME]: ClassiqueHomeDataSchema,
	[SpecialOpsCode.CLUB300]: Club300DataSchema,
	[SpecialOpsCode.DISNEY_HOME]: DisneyHomeDataSchema,
	[SpecialOpsCode.DOLBY_HOME]: DolbyHomeDataSchema,
	[SpecialOpsCode.EMERGENCE]: EmergenceTheaterDataSchema,
	[SpecialOpsCode.EMERGENCE_MOVIE]: EmergenceMovieDataSchema,
	[SpecialOpsCode.EMERGENCE_SERIES]: EmergenceSeriesDataSchema,
	[SpecialOpsCode.EMERGENCE_SVOD]: EmergenceSVODDataSchema,
	[SpecialOpsCode.FESTIVAL_SECTION]: FestivalSectionDataSchema,
	[SpecialOpsCode.FILMES_ONLINE]: FilmesOnlineDataSchema,
	[SpecialOpsCode.IMAX_HOME]: IMAXHomeDataSchema,
	[SpecialOpsCode.INDELEBILE_HOME]: IndelebileHomeDataSchema,
	[SpecialOpsCode.M6_HOME]: M6HomeDataSchema,
	[SpecialOpsCode.MAX_HOME]: MaxHomeDataSchema,
	[SpecialOpsCode.MOBILE_DDAY]: MobileDdayDataSchema,
	[SpecialOpsCode.NOVELAS_HOME]: NovelasHomeDataSchema,
	[SpecialOpsCode.ROLLER_HOME]: RollerHomeDataSchema,
	[SpecialOpsCode.SPONSORED_AUTOCOMPLETE]: SponsoredAutocompleteDataSchema,
	[SpecialOpsCode.TF1_HOME]: TF1HomeDataSchema,
	[SpecialOpsCode.TICKET_ANCHOR]: TicketAnchorDataSchema,
	[SpecialOpsCode.VOD_HOME]: VODHomeDataSchema,
} as const satisfies { [TCode in SpecialOpsCode]: z.ZodObject }

export type SpecialOpsDataByCode = {
	[TCode in SpecialOpsCode]: z.infer<(typeof specialOpsDataSchemaByCode)[TCode]>
}

const unwrapOptionalAndNullable = (schema: z.core.$ZodType): z.core.$ZodType =>
	schema instanceof z.ZodOptional || schema instanceof z.ZodNullable
		? unwrapOptionalAndNullable(schema.unwrap())
		: schema

const SPECIAL_OPS_MEDIAS_KEYS: ReadonlySet<string> = new Set(Object.keys(SpecialOpsMediasSchema.shape))

// True when the data schema has a non-empty `medias` object whose fields are all SpecialOpsMedias keys (desktop,
// desktopHp, mobile) holding a URL, optional or nullable.
export const hasSpecialOpsMediasField = (dataSchema: z.ZodObject) => {
	const shape: z.ZodRawShape = dataSchema.shape
	const medias = shape.medias === undefined ? undefined : unwrapOptionalAndNullable(shape.medias)
	if (!(medias instanceof z.ZodObject)) return false
	const fields = Object.entries(medias.shape)
	return (
		fields.length > 0 &&
		fields.every(
			([key, fieldSchema]) =>
				SPECIAL_OPS_MEDIAS_KEYS.has(key) && unwrapOptionalAndNullable(fieldSchema) instanceof z.ZodURL,
		)
	)
}

const CODES_WITH_SPECIAL_OPS_MEDIAS: ReadonlySet<SpecialOpsCode> = new Set(
	Object.values(SpecialOpsCode).filter((code) => hasSpecialOpsMediasField(specialOpsDataSchemaByCode[code])),
)

// Whether an operation of this code exposes its `data.medias` through the GraphQL `medias` field (SpecialOpsMedias).
export const hasSpecialOpsMedias = (code: SpecialOpsCode) => CODES_WITH_SPECIAL_OPS_MEDIAS.has(code)
